import { personaInitial, personaSubtitle } from '@/lib/persona-labels';
import type { InvolvementLevel, PersonaCard } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';

/**
 * The two blocks a persona's card adds to the common tile (refonte modulaire
 * §15; maquette `Carte`).
 *
 * §15 puts the persona's card in a variant of its own - « avatar et jauge » -
 * and neither block is a sentence, which is why `versioned-card.tsx` grew its
 * two optional slots in this lot. What stays in the layer is everything the
 * four objects share: the pastilles, the usage, the stamp, the greyed-out state
 * of a retired card and the link that wraps it all.
 *
 * ## Why a gauge rather than the line of text §15 first drew
 *
 * « La même question - lequel parle comment, lequel se livre le plus - se lit
 * plus vite à la jauge qu'à la phrase. » The line « présentation · cran · ton »
 * was abandoned at the mockup review of 2026-09-09, and the subtitle keeps the
 * half of it that names who speaks.
 *
 * ## A RETIRED cran carries no mark at all, and that was decided twice
 *
 * The gauge simply has no filled point, and the cran's name still reads in
 * `bleu-nuit` exactly as it would on a live cran. No badge, no alert colour, no
 * fading (§15, and the mockup's own « deux points tranchés à la revue »): a
 * card is read at a glance, and it is the « Cran retiré » filter of the list
 * that finds these personas again.
 */

/** The round avatar: an initial when named, the anonymous pictogram otherwise (§15). */
function PersonaAvatar({ firstName }: { firstName: string | null }) {
  /*
    `aria-hidden`, both branches: the avatar restates the subtitle beside it,
    which already says « Claire · ton chaleureux ». Announced, the card's link
    would read the first name twice, and the anonymous branch would read the
    word « user » - a Lucide file name, not a fact about the persona.
  */
  if (firstName === null) {
    return (
      <span
        aria-hidden="true"
        className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500"
      >
        <Icon name="person" size={24} />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-bleu-nuit-tint font-bold text-bleu-nuit text-body-lg leading-none"
    >
      {personaInitial(firstName)}
    </span>
  );
}

/**
 * What stands beside the chevron: the avatar, the operator's name, the
 * subtitle (§15).
 *
 * The `<h2>` keeps the layer's own classes rather than new ones, so a persona's
 * card and a criterion's read as one register in a library somebody switches
 * between.
 */
export function PersonaCardIdentity({ card }: { card: PersonaCard }) {
  const firstName = card.presentation.kind === 'named' ? card.presentation.firstName : null;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <PersonaAvatar firstName={firstName} />
      <div className="flex min-w-0 flex-col gap-0.5">
        <h2 className="min-w-0 break-words font-semibold text-bleu-nuit text-body-lg leading-7">
          {card.name}
        </h2>
        <p className="min-w-0 break-words text-caption text-gray-600">
          {personaSubtitle(firstName, card.toneName)}
        </p>
      </div>
    </div>
  );
}

/**
 * The mini-gauge: « implication », the scale in miniature, the cran's name
 * (§15).
 *
 * The track is drawn from the CURRENT scale and the filled point is found by
 * IDENTIFIER, which is why `personaCardOf` deliberately does not serve a
 * position: a position frozen in a version would be drawn against a scale that
 * has since gained or lost a cran, and the gauge would point at the wrong dot
 * while looking perfectly healthy.
 *
 * No progress fill under the points, unlike the editor's slider: the card's
 * gauge is read and not aimed at, and a filled segment on six cards in a row
 * reads as six progress bars racing each other.
 */
export function PersonaCardGauge({
  card,
  levels,
}: {
  card: PersonaCard;
  levels: readonly InvolvementLevel[];
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="shrink-0 text-caption text-gray-500 leading-4">implication</span>
      {/*
        `aria-hidden`, and the name beside it is what carries the fact: a row of
        dots has no reading order worth announcing, and « liste de 3 éléments »
        in the middle of a card would be noise around a word already there.
      */}
      <span
        aria-hidden="true"
        className="relative flex h-3 min-w-11 flex-1 items-center justify-between"
      >
        <span className="-translate-y-1/2 absolute inset-x-1 top-1/2 block h-0.5 rounded-full bg-rail" />
        {levels.map((level) =>
          level.id === card.level.levelId ? (
            <span key={level.id} className="relative block size-3 rounded-full bg-bleu-nuit" />
          ) : (
            <span
              key={level.id}
              className="relative block size-2 rounded-full border-2 border-gray-300 bg-white"
            />
          ),
        )}
      </span>
      {/*
        The name comes from the version's FROZEN copy and not from the scale:
        that is what a retired cran still has, and what §15 keeps on the card.
      */}
      <span className="shrink-0 font-semibold text-bleu-nuit text-caption">{card.level.name}</span>
    </div>
  );
}
